import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  Case,
  ChatOwner,
  ChatScope,
  Conversation,
  ConversationPage,
} from "../lib/api";
import { useChatStore } from "../lib/chatStore";
import CaseEdgeRail from "../components/CaseEdgeRail";

type OrderBy = "recent" | "oldest" | "messages" | "title";

interface Filters {
  q: string;
  scope: ChatScope;
  owner_id: number | null;
  date_from: string;
  date_to: string;
  min_messages: number;
  order_by: OrderBy;
  limit: number;
  offset: number;
}

const DEFAULT_FILTERS: Filters = {
  q: "",
  scope: "all",
  owner_id: null,
  date_from: "",
  date_to: "",
  min_messages: 0,
  order_by: "recent",
  limit: 25,
  offset: 0,
};

const PAGE_SIZES = [10, 25, 50, 100];

export default function ChatSearch() {
  const { caseId } = useParams();
  const id = Number(caseId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [kase, setKase] = useState<Case | null>(null);
  const [owners, setOwners] = useState<ChatOwner[]>([]);
  const [page, setPage] = useState<ConversationPage | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const setShared = useChatStore((s) => s.setShared);
  const deleteConversation = useChatStore((s) => s.deleteConversation);
  const loadConversations = useChatStore((s) => s.loadConversations);

  // Filters live in the URL so a Chat Search query is shareable / refresh-safe.
  const filters: Filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const [draftQ, setDraftQ] = useState(filters.q);

  useEffect(() => { setDraftQ(filters.q); }, [filters.q]);

  // Load case + owner list once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, os] = await Promise.all([
          api.get<Case>(`/cases/${id}`),
          api.get<ChatOwner[]>(`/cases/${id}/chats/owners`),
        ]);
        if (!cancelled) {
          setKase(c);
          setOwners(os);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load case");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Re-run search whenever any filter / pagination param changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr(null);
      try {
        const params = new URLSearchParams();
        if (filters.q.trim()) params.set("q", filters.q.trim());
        params.set("scope", filters.scope);
        if (filters.owner_id != null) params.set("owner_id", String(filters.owner_id));
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);
        if (filters.min_messages > 0) params.set("min_messages", String(filters.min_messages));
        params.set("order_by", filters.order_by);
        params.set("limit", String(filters.limit));
        params.set("offset", String(filters.offset));
        const data = await api.get<ConversationPage>(
          `/cases/${id}/chats/search?${params.toString()}`,
        );
        if (!cancelled) setPage(data);
      } catch (e: any) {
        if (!cancelled) {
          setPage(null);
          setErr(e?.message ?? "Search failed");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, filters]);

  function update(patch: Partial<Filters>) {
    // Any *filter* change resets pagination — otherwise we'd silently drop
    // the user onto e.g. page 5 of a smaller result set. Page-size changes
    // also reset offset; only an explicit `offset` patch keeps the offset.
    const next: Filters = { ...filters, ...patch };
    if (!("offset" in patch)) next.offset = 0;
    setSearchParams(filtersToUrl(next), { replace: true });
  }

  function resetFilters() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    update({ q: draftQ });
  }

  async function openChat(c: Conversation) {
    // Make sure the sidebar tab matches the chat's bucket so the user lands
    // in a consistent state (e.g. opening someone else's shared chat from
    // search shouldn't drop them on a still-loading "Mine" tab).
    useChatStore.getState().setScope(id, c.is_owner ? "mine" : "shared");
    await selectConversation(id, c.id);
    // Refresh the destination tab in the background so the sidebar list
    // stays consistent with what the search page is showing.
    void loadConversations(id, undefined, c.is_owner ? "mine" : "shared");
    navigate(`/cases/${id}`);
  }

  async function toggleShare(c: Conversation) {
    await setShared(id, c.id, !c.is_shared);
    setPage((p) =>
      p
        ? {
            ...p,
            items: p.items.map((r) =>
              r.id === c.id ? { ...r, is_shared: !c.is_shared } : r,
            ),
          }
        : p,
    );
  }

  async function onDelete(c: Conversation) {
    if (!confirm(`Delete chat "${displayTitle(c)}"? This cannot be undone.`)) return;
    await deleteConversation(id, c.id);
    // Optimistically drop the row and shrink the total. We don't refetch
    // here so the user keeps their scroll position; the next filter change
    // (or page nav) will sync against the server again.
    setPage((p) =>
      p
        ? {
            ...p,
            items: p.items.filter((r) => r.id !== c.id),
            total: Math.max(0, p.total - 1),
          }
        : p,
    );
  }

  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const pageStart = total === 0 ? 0 : filters.offset + 1;
  const pageEnd = Math.min(filters.offset + items.length, total);
  const hasPrev = filters.offset > 0;
  const hasNext = filters.offset + filters.limit < total;
  const pageNum = Math.floor(filters.offset / filters.limit) + 1;
  const pageCount = total > 0 ? Math.max(1, Math.ceil(total / filters.limit)) : 1;

  return (
    <div className="chat-search">
     <CaseEdgeRail
        caseId={id}
        activeTab="chats"
        totalDocs={kase?.document_count}
        memberCount={kase?.member_count}
        videoCount={kase?.video_count}
      />
     <div className="chat-search__inner">
      <header className="chat-search__head">
        <div>
          <div className="page-eyebrow">Chat search</div>
          <h1>{kase ? `Chats in ${kase.name}` : "Chat search"}</h1>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      <form className="chat-search__filters" onSubmit={onSubmit}>
        <div className="chat-search__filter chat-search__filter--wide">
          <label htmlFor="cs-q">Search</label>
          <input
            id="cs-q"
            type="search"
            placeholder="Title or any question text…"
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
          />
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-scope">Scope</label>
          <select
            id="cs-scope"
            value={filters.scope}
            onChange={(e) => update({ scope: e.target.value as ChatScope })}
          >
            <option value="all">All visible</option>
            <option value="mine">Mine only</option>
            <option value="shared">Shared with me</option>
          </select>
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-owner">Owner</label>
          <select
            id="cs-owner"
            value={filters.owner_id ?? ""}
            onChange={(e) => update({ owner_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Anyone</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>{o.display_name}</option>
            ))}
          </select>
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-from">Active from</label>
          <input
            id="cs-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => update({ date_from: e.target.value })}
          />
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-to">Active to</label>
          <input
            id="cs-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => update({ date_to: e.target.value })}
          />
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-minmsg">Min. messages</label>
          <input
            id="cs-minmsg"
            type="number"
            min={0}
            max={500}
            value={filters.min_messages || ""}
            onChange={(e) => update({ min_messages: Math.max(0, Number(e.target.value || 0)) })}
          />
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-order">Sort by</label>
          <select
            id="cs-order"
            value={filters.order_by}
            onChange={(e) => update({ order_by: e.target.value as OrderBy })}
          >
            <option value="recent">Most recent activity</option>
            <option value="oldest">Oldest activity</option>
            <option value="messages">Most messages</option>
            <option value="title">Title (A→Z)</option>
          </select>
        </div>
        <div className="chat-search__filter">
          <label htmlFor="cs-limit">Per page</label>
          <select
            id="cs-limit"
            value={filters.limit}
            onChange={(e) => update({ limit: Number(e.target.value) || DEFAULT_FILTERS.limit })}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="chat-search__filter chat-search__filter--actions">
          <button type="submit" className="btn btn--primary">Search</button>
          <button type="button" className="btn" onClick={resetFilters}>Reset</button>
        </div>
      </form>

      <div className="chat-search__meta muted">
        {busy && <span>Searching…</span>}
        {!busy && page && total > 0 && (
          <span>
            Showing {pageStart}–{pageEnd} of {total} chat{total === 1 ? "" : "s"}
          </span>
        )}
        {!busy && page && total === 0 && <span>0 results</span>}
      </div>

      <div className="chat-search__results">
        {page && items.length === 0 && !busy && (
          <div className="empty">
            No chats match these filters. Try widening the date range or
            switching scope to <em>All visible</em>.
          </div>
        )}
        {items.map((c) => (
          <article key={c.id} className="chat-search__row">
            <div className="chat-search__row-head">
              <button
                type="button"
                className="chat-search__title-btn"
                onClick={() => openChat(c)}
                title="Open this chat"
              >
                <span className="chat-search__title-text">{displayTitle(c)}</span>
                {c.is_shared && (
                  <span className="chat-search__chip" title={c.is_owner ? "You shared this" : "Shared by another user"}>
                    Shared
                  </span>
                )}
                {c.is_owner ? (
                  <span className="chat-search__chip chat-search__chip--mine">Mine</span>
                ) : (
                  <span
                    className="chat-search__chip chat-search__chip--others"
                    title={c.owner_email ?? undefined}
                  >
                    {c.owner_display_name ?? "Other user"}
                  </span>
                )}
              </button>
            </div>
            {c.last_question && (
              <div className="chat-search__excerpt muted">
                Last asked: {truncate(c.last_question, 180)}
              </div>
            )}
            <div className="chat-search__row-meta">
              <span>{c.message_count} message{c.message_count === 1 ? "" : "s"}</span>
              <span>·</span>
              <span>Last activity {formatTime(c.last_message_at ?? c.updated_at)}</span>
              <span>·</span>
              <span>Created {formatTime(c.created_at)}</span>
            </div>
            <div className="chat-search__row-actions">
              <button type="button" className="btn btn--small" onClick={() => openChat(c)}>
                Open
              </button>
              {c.is_owner && (
                <>
                  <button
                    type="button"
                    className={`btn btn--small${c.is_shared ? " btn--on" : ""}`}
                    onClick={() => void toggleShare(c)}
                  >
                    {c.is_shared ? "Unshare" : "Share with case"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--small btn--danger"
                    onClick={() => void onDelete(c)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>

      {page && total > filters.limit && (
        <div className="chat-search__pager">
          <button
            type="button"
            className="btn"
            disabled={!hasPrev || busy}
            onClick={() => update({ offset: Math.max(0, filters.offset - filters.limit) })}
          >
            ← Previous
          </button>
          <span className="muted">
            Page {pageNum} of {pageCount}
          </span>
          <button
            type="button"
            className="btn"
            disabled={!hasNext || busy}
            onClick={() => update({ offset: filters.offset + filters.limit })}
          >
            Next →
          </button>
        </div>
      )}
     </div>
    </div>
  );
}

function displayTitle(c: Conversation): string {
  return (c.title?.trim() || c.last_question?.trim() || "Untitled chat");
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function formatTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function readFiltersFromUrl(p: URLSearchParams): Filters {
  const scopeRaw = p.get("scope");
  const scope: ChatScope = scopeRaw === "mine" || scopeRaw === "shared" ? scopeRaw : "all";
  const orderRaw = p.get("order_by");
  const order: OrderBy = orderRaw === "oldest" || orderRaw === "messages" || orderRaw === "title"
    ? orderRaw
    : "recent";
  const rawLimit = Number(p.get("limit") ?? DEFAULT_FILTERS.limit);
  const limit = PAGE_SIZES.includes(rawLimit) ? rawLimit : DEFAULT_FILTERS.limit;
  const offset = Math.max(0, Number(p.get("offset") ?? 0) || 0);
  return {
    q: p.get("q") ?? "",
    scope,
    owner_id: p.get("owner_id") ? Number(p.get("owner_id")) : null,
    date_from: p.get("date_from") ?? "",
    date_to: p.get("date_to") ?? "",
    min_messages: Number(p.get("min_messages") ?? 0) || 0,
    order_by: order,
    limit,
    offset,
  };
}

function filtersToUrl(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.scope !== DEFAULT_FILTERS.scope) p.set("scope", f.scope);
  if (f.owner_id != null) p.set("owner_id", String(f.owner_id));
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  if (f.min_messages > 0) p.set("min_messages", String(f.min_messages));
  if (f.order_by !== DEFAULT_FILTERS.order_by) p.set("order_by", f.order_by);
  if (f.limit !== DEFAULT_FILTERS.limit) p.set("limit", String(f.limit));
  if (f.offset > 0) p.set("offset", String(f.offset));
  return p;
}
